<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { Select, Checkbox, SelectOption } from 'ant-design-vue'
  import { AnalysisConfigOption } from '../models/AnalysisConfigOption'

  const props = defineProps({
    modelValue: { type: null, default: undefined }, // value for Select mode
    defaultModelValue: { type: null, default: undefined },
    alternativeModelValue: { type: null, default: undefined }, // value for alternative Select mode
    inactiveFieldDefaultValue: { type: null, default: undefined },
    options: { type: Array<AnalysisConfigOption>, default: () => [] },
    alternativeOptions: { type: Array<AnalysisConfigOption>, default: () => [] },
    placeholder: { type: String, default: '' },
    allowClear: { type: Boolean, default: true },
    toggleLabel: { type: String, default: 'Use custom value' }
  })

  const emit = defineEmits(['update:modelValue', 'update:alternativeModelValue'])

  const computeToggleState = (): boolean => {
    const m = props.modelValue
    const a = props.alternativeModelValue
    const inactive = props.inactiveFieldDefaultValue
    if (m === inactive && a !== inactive) return true
    if (a === inactive && m !== inactive) return false
    return false
  }

  // Local reactive mirrors of incoming props. These are used for v-model binding in the template.
  // Changes to these locals are emitted back to the parent.
  const isToggleEnabled = ref<boolean>(computeToggleState())
  const localSelectValue = ref<any>(props.modelValue ?? props.inactiveFieldDefaultValue)
  const localAltSelectValue = ref<any>(
    props.alternativeModelValue ?? props.inactiveFieldDefaultValue
  )

  // Keep locals in sync when props change
  watch(
    () => props.modelValue,
    (v) => (localSelectValue.value = v),
    { immediate: true }
  )
  watch(
    () => props.alternativeModelValue,
    (v) => (localAltSelectValue.value = v),
    { immediate: true }
  )

  // Emit updates when locals change so parent can bind with v-model:*
  watch(localSelectValue, (v) => emit('update:modelValue', v))
  watch(localAltSelectValue, (v) => emit('update:alternativeModelValue', v))

  // When toggle changes set appropriate inactive/default markers
  const onToggleChange = (checked: boolean) => {
    isToggleEnabled.value = checked

    if (checked) {
      // Enable alternative Select: mark primary as inactive and clear alternative for user selection
      localSelectValue.value = props.inactiveFieldDefaultValue
      localAltSelectValue.value = props.alternativeOptions?.length
        ? props.alternativeOptions[0].value
        : undefined
    } else {
      // Disable alternative: mark alternative as inactive and restore primary default
      localAltSelectValue.value = props.inactiveFieldDefaultValue
      localSelectValue.value = props.defaultModelValue
    }
  }
</script>

<template>
  <div>
    <Checkbox :checked="isToggleEnabled" @change="(e: any) => onToggleChange(e.target.checked)">
      {{ toggleLabel }}
    </Checkbox>

    <div class="mt-2">
      <!-- Alternative Select mode -->
      <Select
        v-if="isToggleEnabled"
        :value="localAltSelectValue"
        @change="(v: any) => (localAltSelectValue = v)"
        :allow-clear="allowClear"
        size="large"
        :placeholder="placeholder || 'Select option'"
        class="w-full"
        showSearch
      >
        <SelectOption
          v-for="option in alternativeOptions"
          :key="option.value"
          :value="option.value"
        >
          <span v-html="option.label"></span>
        </SelectOption>
      </Select>

      <!-- Primary Select mode -->
      <Select
        v-else
        :value="localSelectValue"
        @change="(v: any) => (localSelectValue = v)"
        :allow-clear="allowClear"
        size="large"
        :placeholder="placeholder || 'Select option'"
        class="w-full"
        showSearch
      >
        <SelectOption v-for="option in options" :key="option.value" :value="option.value">
          <span v-html="option.label"></span>
        </SelectOption>
      </Select>
    </div>
  </div>
</template>

<style scoped></style>
